// Document figures tab: lazily loaded figure thumbnails (S3 FigureImage slot) with caption, page, source clause, an Open chart link for figures the charts list of the document knows (one query for the tab) and the digitised curve count for Operator and Admin only, a curves-only filter, keyset paging and the figure dialog.
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, ImageOffIcon } from "lucide-react";
import { useMemo, useState } from "react";

import type { FigureRow } from "@/api/__generated__";
import {
  catalogListFiguresInfiniteOptions,
  curvesListChartsOptions,
} from "@/api/__generated__/@tanstack/react-query.gen";
import { useRegion } from "@/app/region";
import { useInternalDetails } from "@/app/useInternalDetails";
import { ClauseLink } from "@/components/common/ClauseLink";
import { EmptyState } from "@/components/common/EmptyState";
import { ProblemState } from "@/components/common/ProblemState";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { FigureDialog, figureTitle } from "@/features/library/FigureDialog";
import { LazyVisible } from "@/features/library/LazyVisible";
import { docSearch } from "@/features/curves/charts";
import { FigureImage } from "@/features/source/FigureImage";
import { cn, linkClass } from "@/lib/cn";
import { formatNumber, plural } from "@/lib/format";

const PAGE_SIZE = 24;

interface FiguresTabProps {
  docUid: string;
  region: string;
}

const FIGURE_CHART_PREFIX = "fig-";

interface ThumbnailProps {
  figure: FigureRow;
  docUid: string;
  region: string;
  chartKey: string | null;
  onOpen: () => void;
}

function Thumbnail({ figure, docUid, region, chartKey, onOpen }: ThumbnailProps) {
  const internal = useInternalDetails();
  const { currentDocUid } = useRegion();
  return (
    <li>
      <Card className="h-full gap-2.5 p-2.5">
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Open ${figureTitle(figure)}`}
          className="block w-full rounded-sm border-0 bg-transparent p-0"
        >
          <LazyVisible placeholder={<Skeleton className="aspect-[4/3] w-full" />}>
            <FigureImage docUid={docUid} figureKey={figure.key} alt={figureTitle(figure)} dpi={110} />
          </LazyVisible>
        </button>
        <div className="flex min-w-0 flex-col gap-1 px-0.5">
          <span className="line-clamp-2 text-ui font-medium text-foreground">{figureTitle(figure)}</span>
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            <span>p. {figure.page}</span>
            {figure.clause_uid ? (
              <>
                <span aria-hidden>·</span>
                <ClauseLink clauseUid={figure.clause_uid} number={figure.clause_number} />
              </>
            ) : null}
            {(internal && figure.curve_count > 0) || chartKey !== null ? (
              <span className="ml-auto inline-flex items-center gap-2">
                {internal && figure.curve_count > 0 ? (
                  <Badge variant="brand">{plural(figure.curve_count, "curve")}</Badge>
                ) : null}
                {chartKey === null ? null : (
                  <Link
                    to="/$region/curves/$chartKey"
                    params={{ region, chartKey }}
                    search={docSearch(docUid, currentDocUid)}
                    aria-label={`Open chart of ${figureTitle(figure)}`}
                    className={cn(linkClass, "inline-flex items-center gap-1 whitespace-nowrap")}
                  >
                    <ArrowUpRightIcon aria-hidden className="size-3.5" />
                    Open chart
                  </Link>
                )}
              </span>
            ) : null}
          </div>
        </div>
      </Card>
    </li>
  );
}

export function FiguresTab({ docUid, region }: FiguresTabProps) {
  const [curvesOnly, setCurvesOnly] = useState(false);
  const [open, setOpen] = useState<FigureRow | null>(null);
  const request = {
    path: { doc_uid: docUid },
    query: { limit: PAGE_SIZE, has_curve: curvesOnly ? true : null },
  };
  const figures = useInfiniteQuery({
    ...catalogListFiguresInfiniteOptions(request),
    initialPageParam: request,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  });
  const charts = useQuery(curvesListChartsOptions({ path: { doc_uid: docUid } }));
  const chartKeys = useMemo(
    () => new Set((charts.data ?? []).map((chart) => chart.chart_key)),
    [charts.data],
  );
  const chartOf = (figure: FigureRow): string | null => {
    const key = `${FIGURE_CHART_PREFIX}${figure.key}`;
    return chartKeys.has(key) ? key : null;
  };
  const items = figures.data?.pages.flatMap((page) => page.items) ?? [];
  const total = figures.data?.pages[0]?.total ?? 0;

  const content = (() => {
    if (figures.isError) {
      return (
        <Card>
          <ProblemState
            problem={figures.error}
            retry={() => {
              void figures.refetch();
            }}
          />
        </Card>
      );
    }
    if (figures.isPending) {
      return (
        <div aria-hidden className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3.5">
          {Array.from({ length: 8 }, (_, index) => (
            <Skeleton key={index} className="h-60 rounded-lg" />
          ))}
        </div>
      );
    }
    if (items.length === 0) {
      return (
        <Card>
          <EmptyState
            icon={ImageOffIcon}
            title={curvesOnly ? "No figure has digitised curves" : "This document has no figures"}
            description={
              curvesOnly
                ? "Curves are digitised from figures during extraction; none are linked to a figure yet."
                : "No figure was found in the source PDF during ingestion."
            }
          />
        </Card>
      );
    }
    return (
      <>
        <ul className="m-0 grid list-none grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-3.5 p-0">
          {items.map((figure) => (
            <Thumbnail
              key={figure.figure_uid}
              figure={figure}
              docUid={docUid}
              region={region}
              chartKey={chartOf(figure)}
              onOpen={() => {
                setOpen(figure);
              }}
            />
          ))}
        </ul>
        {figures.hasNextPage ? (
          <div className="mt-4 flex justify-center">
            <Button
              variant="outline"
              disabled={figures.isFetchingNextPage}
              onClick={() => {
                void figures.fetchNextPage();
              }}
            >
              {figures.isFetchingNextPage
                ? "Loading…"
                : `Show more (${formatNumber(total - items.length)} left)`}
            </Button>
          </div>
        ) : null}
      </>
    );
  })();

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-ui text-muted-foreground" aria-live="polite">
          {figures.isPending
            ? "Loading figures…"
            : `${plural(total, "figure")}${curvesOnly ? " with digitised curves" : ""}`}
        </p>
        <ToggleGroup
          type="single"
          aria-label="Figures to show"
          value={curvesOnly ? "curves" : "all"}
          onValueChange={(value) => {
            if (value) {
              setCurvesOnly(value === "curves");
            }
          }}
        >
          <ToggleGroupItem value="all">All figures</ToggleGroupItem>
          <ToggleGroupItem value="curves">With curves</ToggleGroupItem>
        </ToggleGroup>
      </div>
      {content}
      <FigureDialog
        docUid={docUid}
        region={region}
        figure={open}
        chartKey={open === null ? null : chartOf(open)}
        onOpenChange={(value) => {
          if (!value) {
            setOpen(null);
          }
        }}
      />
    </div>
  );
}
